import { ApiError, DEMO_ACCOUNTS, type OtpChallenge, type Session } from '@klukka/client';
import { LANGS, t, type LangCode } from '@klukka/core';
import { Ic } from '@klukka/ui';
import { useState } from 'react';
import { api, showDemoHints } from './api';

/**
 * Kennitala first, code second.
 *
 * The first time an account signs in, the kennitala alone is enough — there is
 * nothing sensitive behind it yet and onboarding a field worker has to be
 * frictionless. Once the account is known, every later login needs a code sent
 * to the mobile number on their profile.
 */
export function Login({ lang, onLang, onSignedIn }: {
  lang: LangCode;
  onLang: (l: LangCode) => void;
  onSignedIn: (s: Session) => void;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const [step, setStep] = useState<'kt' | 'otp'>('kt');
  const [kt, setKt] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const fail = (e: unknown, fallback: string) => {
    setErr(e instanceof ApiError && e.code === 'account_not_found' ? tr('ktNotFound') : fallback);
  };

  const submitKt = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const ch = await api.requestOtp(kt, 'employee');
      setChallenge(ch);
      if (ch.firstLogin) {
        onSignedIn(await api.completeFirstLogin(ch.challengeId));
        return;
      }
      setStep('otp');
    } catch (e) {
      fail(e, tr('ktNotFound'));
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async () => {
    if (busy || !challenge) return;
    setBusy(true);
    setErr(null);
    try {
      onSignedIn(await api.verifyOtp(challenge.challengeId, code));
    } catch (e) {
      fail(e, tr('wrongCode'));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setErr(null);
    setCode('');
    try {
      setChallenge(await api.requestOtp(kt, 'employee'));
    } catch (e) {
      fail(e, tr('wrongCode'));
    }
  };

  return (
    <div className="login-page">
      <div className="login">
        {/*
          The two portals are one product on four hostnames, so the brand block
          has to answer "which one have I landed on" before anything else. The
          name says which app, the line under it says who it is for — and the
          card no longer repeats it, because a heading that restates the words
          directly above it is noise on a phone-sized screen.
        */}
        <div className="login-brand">
          <Ic.clock size={44} sw={1.6} />
          <b>Klukka-app</b>
          <span>{tr('loginTitle')}</span>
        </div>

        {step === 'kt' ? (
          <div className="login-box">
            <label className="fld">
              kt.
              <input
                className={`inp${err ? ' err' : ''}`}
                inputMode="numeric"
                autoComplete="username"
                placeholder="000000-0000"
                value={kt}
                onChange={(e) => { setKt(e.target.value); setErr(null); }}
                onKeyDown={(e) => e.key === 'Enter' && void submitKt()}
              />
            </label>
            {err ? <span className="err-msg">{err}</span> : null}
            <button className="btn-main" style={{ width: '100%', marginTop: 14 }} disabled={busy} onClick={() => void submitKt()}>
              {busy ? tr('loading') : tr('continueBtn')}
            </button>
            <div className="dimtx" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.5 }}>{tr('firstLoginNote')}</div>
          </div>
        ) : (
          <div className="login-box">
            <div className="card-title">{tr('otpSent')}</div>
            <div className="dimtx" style={{ fontSize: 13, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Ic.phone size={14} /> {challenge?.maskedPhone}
            </div>
            <label className="fld">
              <input
                className={`inp otp${err ? ' err' : ''}`}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={4}
                placeholder="0000"
                value={code}
                onChange={(e) => { setCode(e.target.value); setErr(null); }}
                onKeyDown={(e) => e.key === 'Enter' && void submitOtp()}
              />
            </label>
            {err ? <span className="err-msg">{err}</span> : null}
            <button className="btn-main" style={{ width: '100%', marginTop: 14 }} disabled={busy} onClick={() => void submitOtp()}>
              {busy ? tr('loading') : tr('continueBtn')}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button className="link-btn" onClick={() => void resend()}>{tr('resend')}</button>
            </div>
            {challenge?.devCode ? <div className="demo-otp">DEMO · SMS: <b>{challenge.devCode}</b></div> : null}
          </div>
        )}

        <button className="lang-btn" onClick={() => setLangOpen(true)}>
          <Ic.globe size={15} /> {LANGS.find(([c]) => c === lang)?.[1] ?? lang.toUpperCase()}
        </button>

        {showDemoHints && step === 'kt' ? (
          <div className="login-hints">
            <b>Demo</b> — tap a kennitala to fill it in:
            {DEMO_ACCOUNTS.employees.map((a) => (
              <div key={a.kennitala}>
                <button onClick={() => { setKt(a.kennitala); setErr(null); }}>{a.kennitala}</button>
                {' '}· {a.name} <span className="dimtx">({a.company})</span>
              </div>
            ))}
          </div>
        ) : null}

        {langOpen ? (
          <div className="sheet-bg" style={{ position: 'fixed' }} onClick={() => setLangOpen(false)}>
            <div className="sheet" onClick={(e) => e.stopPropagation()}>
              <div className="card-title">{tr('language')}</div>
              <div className="lang-grid">
                {LANGS.map(([code, name]) => (
                  <button
                    key={code}
                    className={`lang-opt${lang === code ? ' on' : ''}`}
                    onClick={() => { onLang(code); setLangOpen(false); }}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
